import { AsyncThunk, AsyncThunkPayloadCreator, createAsyncThunk, ThunkAction } from '@reduxjs/toolkit'
import { AnyAction } from 'redux'
import { PortalApiError } from '@afterpaytouch/portal-api/types'
import { HttpService } from '@afterpaytouch/portal-api/api'
import { isPortalAxiosError } from '@afterpaytouch/portal-api/utils'
import type { CheckoutAppDispatch, CheckoutAppState } from '.'
import { FullResponseApiError, GenericApiError } from '../model'

export interface ThunkExtraArgs {
  portalApi: () => HttpService
  topaz: () => HttpService
  paymentsSourceCapture: () => HttpService
  authSessionsApi: () => HttpService
}

export interface CheckoutThunkConfig {
  state: CheckoutAppState
  dispatch: CheckoutAppDispatch
  extra: ThunkExtraArgs
}

export interface PortalThunkConfig extends CheckoutThunkConfig {
  rejectValue: PortalApiError | GenericApiError | FullResponseApiError
}

export type Action<T> = ThunkAction<T, CheckoutAppState, ThunkExtraArgs, AnyAction>

export type AsyncAction<T> = ThunkAction<Promise<T>, CheckoutAppState, ThunkExtraArgs, AnyAction>

export type CheckoutAsyncThunk<Request, Response> = AsyncThunk<Response, Request, CheckoutThunkConfig>

export type PortalThunk<Request, Response> = AsyncThunk<Response, Request, PortalThunkConfig>

export const portalAction = <Request, Response>(
  name: string,
  creator: AsyncThunkPayloadCreator<Response, Request, PortalThunkConfig>,
  returnFullResponse?: boolean,
  enableNonPortalAxiosVerboseErrors?: boolean
): PortalThunk<Request, Response> =>
  // @ts-expect-error: OPERATION BLEED STOPPER
  createAsyncThunk<Response, Request, PortalThunkConfig>(name, async (args, thunk) => {
    try {
      return await creator(args, thunk)
    } catch (e) {
      if (isPortalAxiosError(e)) {
        if (returnFullResponse) {
          return thunk.rejectWithValue({
            status: e.response?.status,
            data: e.response?.data,
          })
        }

        // @ts-expect-error: OPERATION BLEED STOPPER
        return thunk.rejectWithValue(e?.response?.data)
      } else {
        if (enableNonPortalAxiosVerboseErrors === true && typeof e.response !== 'undefined') {
          return thunk.rejectWithValue(e.response?.data)
        }

        const errorMap: GenericApiError = {
          status: e?.status,
          responseHeaders: typeof e?.response?.headers !== 'undefined' ? { ...e.response.headers } : undefined,
          message: e?.message,
          requestUrl: e?.config?.url,
          responseData: e?.response?.data,
          code: e?.code,
          name: e?.name,
          stack: e?.stack,
        }

        return thunk.rejectWithValue(errorMap)
      }
    }
  })
